import { useState } from "react";
import { Search, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AdminUserLookupProps {
  onImpersonationStart: (email: string) => void;
}

export const AdminUserLookup = ({ onImpersonationStart }: AdminUserLookupProps) => {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleImpersonate = async () => {
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a user email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Impersonation uses a recovery magic link returned from the backend

      // Call the impersonate-user edge function
      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { 
          targetEmail: email.trim(),
          reason: reason.trim() || null,
          originUrl: window.location.origin,
        },
      });

      if (error) {
        console.error('Impersonation error:', error);
        throw new Error(error.message || 'Failed to impersonate user');
      }

      if (!data?.actionLink || !data?.adminRecoveryLink) {
        throw new Error('Missing impersonation links from server');
      }

      // Store impersonation state and admin recovery link
      sessionStorage.setItem('impersonation_active', 'true');
      sessionStorage.setItem('impersonation_target', email.trim());
      sessionStorage.setItem('admin_recovery_link', data.adminRecoveryLink);

      // Notify parent component
      onImpersonationStart(email.trim());

      // Navigate to the magic link to switch sessions
      window.location.href = data.actionLink;

    } catch (error: any) {
      console.error('Error impersonating user:', error);
      toast({
        title: "Impersonation failed",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCircle2 className="h-5 w-5" />
          Login as User
        </CardTitle>
        <CardDescription>
          Impersonate a user account for support purposes. All actions will be logged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="user-email">User Email</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="user-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reason">Reason (Optional)</Label>
          <Textarea
            id="reason"
            placeholder="e.g., Customer support request #12345"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            disabled={loading}
          />
        </div>

        <Button
          onClick={handleImpersonate}
          disabled={loading || !email.trim()}
          className="w-full"
        >
          {loading ? "Starting impersonation..." : "Login as User"}
        </Button>

        <div className="rounded-md bg-muted p-3 text-sm">
          <p className="font-medium text-foreground">Security Notes:</p>
          <ul className="mt-1 space-y-1 text-muted-foreground">
            <li>• Cannot impersonate other admin accounts</li>
            <li>• All actions are logged with timestamp and reason</li>
            <li>• A red banner will indicate impersonation mode</li>
            <li>• Click "Exit" in the banner to return to your admin account</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
